/**
 * Runtime values from `/config.json` (see loadRuntimeConfig). Dev, preview, and Docker
 * serve the same JSON shape.
 */

const DEFAULT_PUBLIC_SITE_URL = 'https://stockstars.app'
const DEV_ADMIN_PAGE_URL = 'http://localhost:3715/#/signin'

/** Production static assets on Cloudflare (R2 custom domain). */
const DEFAULT_ASSETS_BASE_URL = 'https://assets.stockstars.app'

let runtimeApiOrigin = null
let runtimePublicSiteUrl = null
let runtimeAssetsBaseUrl = null
let runtimeAdminUrl = null
let runtimePricingPage = null
let runtimeSubscriptionMgmtLink = null
let runtimeMembersSupportEmail = null

export function setRuntimeApiOrigin(origin) {
  runtimeApiOrigin = origin
}

export function setRuntimePublicSiteUrl(url) {
  runtimePublicSiteUrl = url
}

export function setRuntimeAssetsBaseUrl(url) {
  runtimeAssetsBaseUrl = url
}

export function setRuntimeAdminUrl(url) {
  runtimeAdminUrl = url
}

export function setRuntimePricingPage(template) {
  runtimePricingPage = template
}

export function setRuntimeSubscriptionMgmtLink(link) {
  runtimeSubscriptionMgmtLink = link
}

export function setRuntimeMembersSupportEmail(email) {
  runtimeMembersSupportEmail = email
}

export function resetRuntimeConfigForTests() {
  runtimeApiOrigin = null
  runtimePublicSiteUrl = null
  runtimeAssetsBaseUrl = null
  runtimeAdminUrl = null
  runtimePricingPage = null
  runtimeSubscriptionMgmtLink = null
  runtimeMembersSupportEmail = null
}

function normalizeOrigin(raw) {
  if (raw == null) return ''

  const t = String(raw).trim()
  if (t === '') return ''

  return t.replace(/\/+$/, '')
}

/**
 * Public API origin (scheme + host + optional port), no trailing slash.
 * Returns '' in dev so Vite proxy handles API requests.
 */
export function getApiBaseUrl() {
  if (import.meta.env.DEV) {
    return ''
  }
  if (runtimeApiOrigin != null && runtimeApiOrigin !== '') {
    return normalizeOrigin(runtimeApiOrigin)
  }
  return ''
}

/**
 * API origin from runtime config regardless of dev/prod (consent-law-region fetch).
 */
export function getConfiguredApiOrigin() {
  return normalizeOrigin(runtimeApiOrigin)
}

/**
 * Marketing site base URL, or default when unset.
 */
export function getRuntimePublicSiteBaseUrl() {
  if (runtimePublicSiteUrl != null && runtimePublicSiteUrl !== '') {
    return normalizeOrigin(runtimePublicSiteUrl)
  }
  return DEFAULT_PUBLIC_SITE_URL
}

/**
 * CDN / static-assets origin for images (no trailing slash).
 * Empty string means same-origin relative paths (local `public/` in Vite dev).
 */
export function getAssetsBaseUrl() {
  if (runtimeAssetsBaseUrl != null && runtimeAssetsBaseUrl !== '') {
    return normalizeOrigin(runtimeAssetsBaseUrl)
  }
  if (import.meta.env.DEV) {
    return ''
  }
  return DEFAULT_ASSETS_BASE_URL
}

export function getAdminPageUrl() {
  if (runtimeAdminUrl != null && runtimeAdminUrl !== '') {
    return runtimeAdminUrl.trim()
  }
  if (import.meta.env.DEV) {
    return DEV_ADMIN_PAGE_URL
  }
  console.error(
    'ADMIN_URL is not configured. Set it in the container environment or .env file.',
  )
  return ''
}

export function getPricingPageTemplate() {
  if (runtimePricingPage != null && runtimePricingPage !== '') {
    return runtimePricingPage.trim()
  }
  return ''
}

export function getSubscriptionMgmtLink() {
  if (
    runtimeSubscriptionMgmtLink != null && runtimeSubscriptionMgmtLink !== ''
  ) {
    return runtimeSubscriptionMgmtLink.trim()
  }
  return ''
}

export function getMembersSupportEmail() {
  if (runtimeMembersSupportEmail != null && runtimeMembersSupportEmail !== '') {
    return runtimeMembersSupportEmail.trim()
  }
  return ''
}

export function assertApiOriginConfigured() {
  if (getApiBaseUrl() === '') {
    const msg =
      'API origin is required. Set API_ORIGIN in .env.development (dev: Vite serves /config.json). ' +
      'Production: set API_ORIGIN on the container so /config.json is generated.'
    console.error(msg)
    throw new Error(msg)
  }
}
